import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Resources, User } from 'ri-api-client';
import { Observable } from 'rxjs/Observable';
import { ConfigService } from '../config.service';
import { ApiService } from './api.service';
import { NotificationService } from './api/notification.service';
import { UserService } from './api/user.service';
import { LocalStorageService } from 'angular-2-local-storage';
import { LanguageService } from './language.service';
import * as objectPath from 'object-path';
import { mergeMap, take } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';


@Injectable()
export class AuthService implements OnInit {

  public user: User.User;
/**
  * Variable created to use at Kb component.
*/
  public choiceUser: boolean;
  public invalidLogin: string; // invalid login tracking variable
  public passwordExpired: boolean; // for the purpose of loginExpired page
  public expiredPasswordEmail: string; // for the purpose of loginExpired page

  constructor(
    private router: Router,
    private _user: UserService,
    private oAuth: OAuthService,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private apiService: ApiService,
    private _http: HttpClient,
    private _localStorage: LocalStorageService
  ) {
    this.invalidLogin = null;
    this.passwordExpired = false;
    this.expiredPasswordEmail = null;
    this.oAuth.clientId = 'riapps';
    this.oAuth.scope = 'email';
    this.oAuth.issuer = ConfigService.app.oauthServiceIssuer;
    this.oAuth.dummyClientSecret = 'dummySecret';
    this.oAuth.oidc = false;
    // this.oauthService.requireHttps = false;
    this.oAuth.loadDiscoveryDocument().then(() => { });
  }

  ngOnInit() {}

  SignInExternal(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      // We are hacking oauthService by manually forcing our token. It's ugly but temporary, while implicitFlow is not working
      this.getToken().subscribe( token => {
        this.storeAccessTokenResponse(token.access_token, token.expires_in);
        this.loginSuccessful();
      });
    } else {
      window.location.href = `${ConfigService.app.oauthExternal}/signin?returnUrl=${window.location.href}?authenticated=true`;
    }
  }

  private getToken(): Observable<any> {
    const url = `${ConfigService.app.oauthExternal}/mytoken`;
    // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
    return this._http.get(url, { responseType: 'json', withCredentials: true });
  }
  private storeAccessTokenResponse = function (accessToken, expiresIn) {
    sessionStorage.setItem('access_token', accessToken);
    sessionStorage.setItem('access_token_stored_at', '' + Date.now());
    if (expiresIn) {
      const expiresInMilliSeconds = expiresIn * 1000;
      const now = new Date();
      const expiresAt = now.getTime() + expiresInMilliSeconds;
      sessionStorage.setItem('expires_at', '' + expiresAt);
    }
  };

  login(loginForm: NgForm): void {
    this.passwordExpired = false;
    this.expiredPasswordEmail = null;
    this.oAuth
      .fetchTokenUsingPasswordFlow(loginForm.value.userName, loginForm.value.password)
        .then(() => {
          this.loginSuccessful();
        })
        .catch((err) => {
          const error = JSON.parse(err._body).error_description;
          if (error === 'PASSWORD_EXPIRED') {
            this.passwordExpired = true;
            this.expiredPasswordEmail = loginForm.value.userName;
            this.router.navigate(['/loginExpired']);
          } else {
            this.invalidLogin = error;
          }
        });
  }

  private loginSuccessful(): void {
    this.invalidLogin = null;
    this.passwordExpired = false;
    this.apiService.init();
    this.setAuthenticatedUser();
    const customerDetailsId: any = this._localStorage.get('customerDetailsId');

    this._user.getCurrent().pipe(take(1), mergeMap((user: User.User) => {
      if (user && user.pinnedAppIds && user.pinnedAppIds.length > 0) {
        const appId: number = user.pinnedAppIds[0];
        return this.apiService.client.apps.id(appId.toString()).GET();
      } else {
        const noAppsInList = new Observable((observer) => {
          observer.next({ noAppsInList: true});
          observer.complete();
        });
        return noAppsInList.pipe(take(1));
      }
    })).subscribe((apps: Resources.Apps.AppsId | any) => {
      if (customerDetailsId && customerDetailsId !== '') {
        this.router.navigate(['customerAnalysis']);
      } else {
        if (apps && apps.bodyRaw && apps.bodyRaw.link) {
          this.router.navigate([`/${apps.bodyRaw.link}`])
        } else {
          this.router.navigate(['']);
        }
      }
    });
  }

  logout(): void {
    this.user = null;
    sessionStorage.clear();
    this.apiService.destroy();
    this.v4Logout();
    this.oAuth.logOut(true);

    if (ConfigService.app.oauthExternal) {
      window.location.href = `${ConfigService.app.oauthExternal}/signout`;
      return;
    } else {
      this.router.navigate(['login']);
    }
  }

  requestPasswordReset(email: string): Observable<Object> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    const body = new HttpParams().set('email', email); // .set('baseUri', window.location.href);
    const url = `${ConfigService.app.oauthServiceIssuer}/password/reset`;

    // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
    return this._http.post(url, body, { headers: headers, responseType: 'text' });
  }

  requestPasswordChange(email: string, token: string, password: string): Observable<Object> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    const body = new HttpParams().set('email', email).set('token', token).set('newPassword', password);
    const url = `${ConfigService.app.oauthServiceIssuer}/password/change`;

    // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
    return this._http.post(url, body, { headers: headers, responseType: 'text' });
  }

  requestExpiredPasswordChange(email: string, oldpass: string, newpass: string): Observable<Object> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

    const body = new HttpParams().set('email', email).set('oldPassword', oldpass).set('newPassword', newpass);
    const url = `${ConfigService.app.oauthServiceIssuer}/password/change`;

    // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
    return this._http.post(url, body, { headers: headers, responseType: 'text' });
  }

  v4Login(): Promise<string> {
    if (ConfigService.app.v4AuthenticatorBaseUri) {
      let headers = new HttpHeaders();
      headers = headers.set('Authorization', 'Bearer ' + this.oAuth.getAccessToken());

      const body = new HttpParams();
      const url = `${ConfigService.app.v4AuthenticatorBaseUri}/api/authenticator/login`;

      // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
      return this._http.post(url, body, { headers: headers, responseType: 'text' }).toPromise();
    }
    return null;
  }

  v4Logout() {
    if (ConfigService.app.v4AuthenticatorBaseUri) {
      let headers = new HttpHeaders();
      headers = headers.set('Authorization', 'Bearer ' + this.oAuth.getAccessToken());

      const body = new HttpParams();
      const url = `${ConfigService.app.v4AuthenticatorBaseUri}/api/authenticator/logout`;

      // Angular has a bug with empty 200 responses - see https://github.com/angular/angular/issues/18680
      this._http.post(url, body, { headers: headers, responseType: 'text' }).subscribe();
    }
  }

  // PO-5573 - logout after X minutes of inactivity
  checkForInactivity(lastAction: Date): void {
    if (lastAction == null || !ConfigService.app.idleTimeoutInMinutes) { return };
    const currentDate = new Date();
    const dateDiff = currentDate.valueOf() - lastAction.valueOf();
    if (dateDiff >= (ConfigService.app.idleTimeoutInMinutes * 60 * 1000)) { this.logout(); }
  }

  isAuthenticated(): Promise<any> {
    return new Promise(resolve => {
      if (!this.oAuth.hasValidAccessToken()) {
        this.user = null;
      }
      resolve(this.oAuth.hasValidAccessToken());
    });
  }

  isAccesTokenValid(): boolean {
    return this.oAuth.hasValidAccessToken();
  }
  checkTokenAndSetUser() {
    if (this.isAccesTokenValid()) {
      this.setAuthenticatedUser();
    }
  }
  private setAuthenticatedUser() {
    this._user.getCurrent().subscribe(
      user => {
        this.user = user;
        this.languageService.setUILanguage(this.user.locale);
        // TODO: please, we need to have a better way to identify Choice users...
        if (this.user.email.search('@choice') === -1) {
          this.choiceUser = false;
        } else {
          this.choiceUser = true;
        }
      },
      (err) => {
        if (err.code && err.code === 401) {
          this.logout();
        }
        this.translate.get(objectPath.get(err, 'message', 'ERROR_MESSAGE')).subscribe((text: string) => {
          this.notificationService.showNotification(text);
        });
      }
    );
  }

}



// WEBPACK FOOTER //
// ./src/app/core/auth.service.ts